import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Tabs, Tab } from "react-bootstrap";
import { apiCall } from "../../../utils/axiosService";
import { API_URL } from "../../../utils/apiRoutes";
import { apiResponse } from "../../../utils/apiResponse";
import { errorToastMessage } from "../../../utils/toastMessage";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
// import commonService from "../../../utils/commonService";

import DSDashboard from "./DSDashboard";
import KPIWeights from "./KPIWeights";
import BookingWindow from "./BookingWindow";
import Cancellation from "./Cancellation";
import DemandLevels from "./DemandLevels";
import { commonService } from "../../../utils/commonService";

const DEFAULT_DEMAND_LEVELS = {
    veryhigh: 0,
    high: 0,
    moderate: 0,
    low: 0,
    verylow: 0,
};

const DEFAULT_CANCELLATION = {
    beta: 0.05,
    method: "blended",
};

const toInt = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
};

const getPropertyRef = (propertyDetail, id) => {
    const propertyid = propertyDetail?.propertyid || (id ? parseInt(id, 10) : 0);
    const propertycode = String(propertyDetail?.propertycode || "");
    return {
        propertyid: Number.isNaN(propertyid) ? 0 : propertyid,
        propertycode,
    };
};

const DemandScore = ({ api, para, setting }) => {
    const { id } = useParams();
    const propertyDetail = useSelector(
        (store) => store.headerProperty.propertyDetail,
    );
    const propertyAllDetail = useSelector(
        (store) => store.headerProperty,
    );

    const propertyCode = para?.propertyCode || para?.PropertyCode || para?.propertycode || propertyDetail?.propertycode || "";
    const propertyId = para?.propertyId || para?.PropertyId || para?.propertyid || propertyDetail?.propertyid || 0;

    const [activeTab, setActiveTab] = useState("Dashboard");
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [loadingDemand, setLoadingDemand] = useState(api === 0 ? false : true);

    const [kpiData, setKpiData] = useState([]);
    const [demandLevels, setDemandLevels] = useState(DEFAULT_DEMAND_LEVELS);
    const [cancellationConfig, setCancellationConfig] =
        useState(DEFAULT_CANCELLATION);

    // Demand score data
    const [demandScoreData, setDemandScoreData] = useState(null);
    const [asOfDate, setAsOfDate] = useState(para?.asOfDate || "");

    // Date range
    const [startDate, setStartDate] = useState(() => new Date());
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 13);
        return d;
    });
    const [activeSelectedDate, setActiveSelectedDate] = useState("14 Days");

    
    // ── Fetch asOfDate ───────────────────────────────────────────────
    const getAsOfDate = useCallback(async () => {
        if (api === 0) return;
        try {
            const res = await apiCall(
                {
                    method: "POST",
                    url: API_URL.BASE_API_URL.SnapShot + API_URL.AS_OF_DATE.AS_OF_DATE,
                    body: { propertyCode: propertyCode },
                },
                false,
            );

            if (res?.status_code === 1) {
                const dateVal = res?.data?.[0]?.last_data_refresh || "";
                setAsOfDate(dateVal);
                if (dateVal) {
                    const baseDate = new Date(dateVal);
                    setStartDate(baseDate);
                    
                    const newEnd = new Date(baseDate);
                    newEnd.setDate(baseDate.getDate() + 13);
                    setEndDate(newEnd);
                    setActiveSelectedDate("14 Days");
                }
                return dateVal;
            }
            errorToastMessage("Failed to fetch as-of date");
            return "";
        } catch (err) {
            errorToastMessage("Error fetching as-of date");
            return "";
        }
    }, [propertyCode, api]);

    // ── Fetch KPI list ───────────────────────────────────────────────
    const fetchKPIList = useCallback(async () => {
        if (api === 0) return;
        if (!propertyId) return;

        const res = await apiCall(
            {
                method: "POST",
                url: API_URL.BASE_API_URL.RevenueManagement + API_URL.KPI.GET_LIST,
                body: { propertyid: propertyId, propertycode: propertyCode },
            },
            false,
        );

        const parsed = apiResponse(false, res);
        if (parsed?.isValidate && parsed?.data?.data?.dataList1) {
            const mapped = parsed.data.data.dataList1.map((item) => ({
                kpi: item.kpi || "",
                isimpact: !!item.isimpact,
                category: item.category || "",
                weightage: Number(item.weightage || 0),
                weightage0to13: Number(item.weightage0to13 || 0),
                weightage14to29: Number(item.weightage14to29 || 0),
                weightage30to59: Number(item.weightage30to59 || 0),
                weightage60plus: Number(item.weightage60plus || 0),
                weightagemin: 0,
                weightagemax: 100,
                details: item.details || "",
            }));
            setKpiData(mapped);
        } else {
            setKpiData([]);
        }
    }, [propertyId, propertyCode, api]);

    // ── Fetch cancellation & demand levels ───────────────────────────
    const fetchCancellationAndLevels = useCallback(async () => {
        if (api === 0) return;
        if (!propertyId) return;

        const res = await apiCall(
            {
                method: "POST",
                url:
                    API_URL.BASE_API_URL.RevenueManagement +
                    API_URL.DEMAND_SCORE.GET_CANCELATION_AND_DEMAND_LEVEL_DATA,
                body: { propertyid: propertyId, propertycode: propertyCode },
            },
            false,
        );

        const parsed = apiResponse(false, res);
        if (!parsed?.isValidate) {
            setCancellationConfig(DEFAULT_CANCELLATION);
            setDemandLevels(DEFAULT_DEMAND_LEVELS);
            return;
        }

        const row = parsed?.data?.data || {};
        setCancellationConfig({
            beta: Number(row.beta ?? DEFAULT_CANCELLATION.beta),
            method: row.method ?? DEFAULT_CANCELLATION.method,
        });
        setDemandLevels({
            veryhigh: toInt(row.veryhigh),
            high: toInt(row.high),
            moderate: toInt(row.moderate),
            low: toInt(row.low),
            verylow: toInt(row.verylow),
        });
    }, [propertyId, propertyCode, api]);


    const fetchDemandScoreData = useCallback(async () => {
        if (api === 0) return;
        // Early returns - guard clauses
        if (!asOfDate || !startDate || !endDate || !propertyCode) {
            return;
        }

        // Decide in which tabs we want to load data
        const allowedTabs = ["Dashboard", "Cancelation"]; // ← fix spelling if needed
        if (!allowedTabs.includes(activeTab)) {
            return;
        }

        setLoadingDemand(true);

        try {
            const payload = {
                asofdate: asOfDate,
                propertyCode: propertyCode,
                scoretype: cancellationConfig.method,
                startdate: commonService.getDateForNode(startDate),
                enddate: commonService.getDateForNode(endDate),
                kpiListPara: kpiData.map((item) => ({
                    kpi: item.kpi,
                    weightage: item.weightage,
                    propertycode: propertyCode,
                    category: item.category,
                    weightage0to13: item.weightage0to13,
                    weightage14to29: item.weightage14to29,
                    weightage30to59: item.weightage30to59,
                    weightage60plus: item.weightage60plus,
                })),
                kpiConfigPara: {
                    veryhigh: demandLevels.veryhigh || 0,
                    high: demandLevels.high || 0,
                    moderate: demandLevels.moderate || 0,
                    low: demandLevels.low || 0,
                    verylow: demandLevels.verylow || 0,
                    beta:
                        cancellationConfig.method !== "multiply"
                            ? cancellationConfig.beta
                            : 0,
                    propertycode: propertyCode,
                    propertyid: propertyId || 82, // ← magic number?
                },
            };

            const res = await apiCall(
                {
                    method: "POST",
                    url: `${API_URL.BASE_API_URL.SnapShot}${API_URL.DEMAND_SCORE.GET_DASHBOARD_DATA}`,
                    body: payload,
                },
                false, // ← what does false mean here? consider naming the param
            );

            const parsed = apiResponse(false, res);

            if (!parsed?.isValidate) {
                setDemandScoreData([]);
                // errorToastMessage(parsed?.message || "Failed to load demand score data");
                return;
            }

            // ── Better OTB merging logic ────────────────────────────────────────
            const otbItems =
                parsed?.data?.data?.find((item) => Array.isArray(item?.otb))?.otb || [];

            const otbByDate = new Map(
                otbItems
                    .filter((otb) => otb?.Dates)
                    .map((otb) => [
                        otb.Dates,
                        {
                            RoomSold: otb.RoomSold,
                            Occperc: otb.Occperc,
                            ADR: otb.ADR,
                        },
                    ]),
            );

            const resultArray = (parsed?.data?.data || [])
                .filter((item) => item && item.StayDate) // skip invalid entries
                .map((item) => {
                    const { otb, ...clean } = item;
                    const otbData = otbByDate.get(clean.StayDate);
                    return otbData ? { ...clean, ...otbData } : clean;
                });

            setDemandScoreData(resultArray);
        } catch (err) {
            setDemandScoreData([]);
            // errorToastMessage("Error loading demand score data");
        } finally {
            setLoadingDemand(false);
        }
    }, [
        asOfDate,
        startDate,
        endDate,
        propertyCode, // more stable dep
        propertyId,
        activeTab,
        cancellationConfig?.method,
        // cancellationConfig?.beta,
        kpiData, // ← if this changes often → consider memoizing
        demandLevels,
        api
    ]);
    const fetchAll = useCallback(async () => {
        setLoadingConfig(true);
        try {
            await Promise.all([
                fetchKPIList(),
 
                fetchCancellationAndLevels(),
            ]);
        } catch {
            // errorToastMessage("Failed to load configuration");
        } finally {
            setLoadingConfig(false);
        }
    }, [fetchKPIList, fetchCancellationAndLevels]);

    // ── Load config on mount ─────────────────────────────────────────
    useEffect(() => {
        if (api === 0) return;
        if (propertyCode) {
            getAsOfDate();
            fetchAll();
        }
    }, [propertyCode, getAsOfDate, fetchAll]);

    // ── Watch for global asOfDate changes ────────────────────────────
    useEffect(() => {
        if (api === 0) return;
        if (propertyAllDetail?.asOfDate) {
            const newAsOfDate = propertyAllDetail.asOfDate;
            setAsOfDate(newAsOfDate);
            
            const baseDate = new Date(newAsOfDate);
            setStartDate(baseDate);
            
            const newEnd = new Date(baseDate);
            newEnd.setDate(baseDate.getDate() + 13);
            setEndDate(newEnd);
            setActiveSelectedDate("14 Days");
        }
    }, [propertyAllDetail?.asOfDate]);

    // ── Re-fetch demand data when dependencies change ────────────────
    useEffect(() => {
        if (api === 0) return;
        if (asOfDate && kpiData.length > 0) {
            fetchDemandScoreData();
        }
    }, [asOfDate, startDate, endDate, kpiData, fetchDemandScoreData, propertyAllDetail?.asOfDate]);

    // ── Date range handlers ──────────────────────────────────────────
    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setActiveSelectedDate("");
    };

    const handlePresetClick = (preset) => {
        const baseDate = asOfDate ? new Date(asOfDate) : new Date();
        let newEnd = new Date(baseDate);

        if (preset === "7 Days") newEnd.setDate(baseDate.getDate() + 6);
        else if (preset === "14 Days") newEnd.setDate(baseDate.getDate() + 13);
        else if (preset === "30 Days") newEnd.setDate(baseDate.getDate() + 29);

        setStartDate(baseDate);
        setEndDate(newEnd);
        setActiveSelectedDate(preset);
    };

    // ── KPI update handler ───────────────────────────────────────────
    const handleKPIUpdate = (index, field, value) => {
        setKpiData((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: Number(value) || 0 } : item,
            ),
        );
    };

    // ── Cancellation config update ───────────────────────────────────
    const handleCancellationUpdate = (field, value) => {
        setCancellationConfig((prev) => ({
            ...prev,
            [field]: field === "beta" ? Number(value) : value,
        }));
    };

    // ── Demand levels update ─────────────────────────────────────────
    const handleDemandLevelsUpdate = (field, value) => {
        setDemandLevels((prev) => ({
            ...prev,
            [field]: toInt(value, prev[field]),
        }));
    };

    // ── Save cancellation & demand levels ────────────────────────────
    const handleSaveCancelationLevels = async () => {
        if (!propertyId) return;

        const payload = {
            veryhigh: demandLevels.veryhigh,
            high: demandLevels.high,
            moderate: demandLevels.moderate,
            low: demandLevels.low,
            verylow: demandLevels.verylow,
            beta: cancellationConfig.beta,
            propertycode: propertyCode,
            propertyid: propertyId,
        };

        try {
            const res = await apiCall(
                {
                    method: "POST",
                    url:
                        API_URL.BASE_API_URL.RevenueManagement +
                        API_URL.DEMAND_SCORE.SAVE_CANCELATION_AND_DEMAND_LEVEL_DATA,
                    body: payload,
                },
                true,
            );

            const parsed = apiResponse(false, res);
            if (parsed?.isValidate) {
                // success - maybe show toast
            } else {
                errorToastMessage("Failed to save demand levels / cancellation config");
            }
        } catch (err) {
            errorToastMessage("Error saving demand levels / cancellation config");
        }
    };

    // ── Save all configuration ───────────────────────────────────────
    const handleSave = async () => {
        if (api === 0) return;
        if (!propertyId) {
            errorToastMessage("No property selected");
            return;
        }

        // Validate Booking Window totals do not exceed 100%
        const isPercentScale = kpiData.some(
            (item) =>
                Number(item.weightage0to13) > 1 ||
                Number(item.weightage14to29) > 1 ||
                Number(item.weightage30to59) > 1 ||
                Number(item.weightage60plus) > 1,
        );

        const normalize = (val) => {
            const num = Number(val) || 0;
            return isPercentScale ? num / 100 : num;
        };

        const sum0to13 = kpiData.reduce((sum, item) => sum + normalize(item.weightage0to13), 0);
        const sum14to29 = kpiData.reduce((sum, item) => sum + normalize(item.weightage14to29), 0);
        const sum30to59 = kpiData.reduce((sum, item) => sum + normalize(item.weightage30to59), 0);
        const sum60plus = kpiData.reduce((sum, item) => sum + normalize(item.weightage60plus), 0);

        if (sum0to13 > 1.005) {
            errorToastMessage("Booking Window total for 0-13 days cannot exceed 100%");
            return;
        }
        if (sum14to29 > 1.005) {
            errorToastMessage("Booking Window total for 14-29 days cannot exceed 100%");
            return;
        }
        if (sum30to59 > 1.005) {
            errorToastMessage("Booking Window total for 30-59 days cannot exceed 100%");
            return;
        }
        if (sum60plus > 1.005) {
            errorToastMessage("Booking Window total for 60+ days cannot exceed 100%");
            return;
        }

        setLoadingConfig(true);
        try {
            // 1. Save KPI weights
            const kpiPayload = kpiData.map((row) => ({
                kpi: row.kpi?.trim() || "",
                isimpact: !!row.isimpact,
                category: row.category || "",
                weightage: toInt(row.weightage),
                weightage0to13: toInt(row.weightage0to13),
                weightage14to29: toInt(row.weightage14to29),
                weightage30to59: toInt(row.weightage30to59),
                weightage60plus: toInt(row.weightage60plus),
                propertyid: propertyId,
                propertycode: propertyCode,
            }));

            const kpiRes = await apiCall(
                {
                    method: "POST",
                    url: API_URL.BASE_API_URL.RevenueManagement + API_URL.KPI.SAVE,
                    body: { F: kpiPayload },
                },
                true,
            );

            const kpiParsed = apiResponse(true, kpiRes);
            if (!kpiParsed?.isValidate) {
                errorToastMessage("Failed to save KPI weights");
            }

            // 2. Save cancellation + demand levels
            await handleSaveCancelationLevels();

            // Optional: refresh data after save
            await fetchAll();
            await fetchDemandScoreData();
        } catch (err) {
            errorToastMessage("Error saving configuration");
        } finally {
            setLoadingConfig(false);
        }
    };

    return (
        <div className="tabComponentMain">
            {propertyCode ? (
                <Container fluid>
                    <Row>
                    <Col md={8}>
                        <div className="d-flex align-items-center flex-wrap gap-3">
                            <div className="form-groups from-groups no-border-break meghaDatePicker width225">
                                <DatePicker id={commonService.generateGlobalClickTrackerID("DemandScore", "datepicker-onchange", "")}
                                    portalId="datepicker-root"
                                    onChange={handleDateChange}
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={asOfDate ? new Date(asOfDate) : new Date()}
                                    maxDate={
                                        asOfDate 
                                            ? new Date(new Date(asOfDate).setDate(new Date(asOfDate).getDate() + 90))
                                            : new Date(new Date().setDate(new Date().getDate() + 90))
                                    }
                                    selectsRange
                                    monthsShown={2}
                                />
                            </div>

                            <div className="custom-range-picker d-flex gap-2">
                                {["7 Days", "14 Days", "30 Days"].map((item) => (
                                    <button id={commonService.generateGlobalClickTrackerID("DemandScore", `${item}-button-onclick`, item)}
                                        key={item}
                                        type="button"
                                        className={`preset-btn text-center ${activeSelectedDate === item ? "active" : ""}`}
                                        onClick={() => handlePresetClick(item)}
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>


                        </div>
                    </Col>
                    <Col md={4} >
                        <div className="btn-demand-score-save float-end">
                            {loadingConfig ? (
                                <button className="default-btn primary-btn">Saving...</button>
                            ) : (
                                <button id={commonService.generateGlobalClickTrackerID("DemandScore", "save-configuration", "")}
                                    className="default-btn primary-btn"
                                    onClick={handleSave}
                                    disabled={loadingConfig}
                                >
                                    Save Configuration
                                </button>
                            )}
                        </div>
                    </Col>
                    <Col xl={12} >
                        <Tabs
                            activeKey={activeTab}
                            id="demand-score-tabs"
                            variant="underline"
                            onSelect={(k) => setActiveTab(k)}
                            className="mt-4"
                        >
                            <Tab eventKey="Dashboard" title="Dashboard">
                                {activeTab === "Dashboard" && (
                                    <DSDashboard
                                        fetchAll={fetchAll}
                                       
                                        dependentReqData={{
                                            kpiData,
                                            demandLevels,
                                            cancellationConfig,
                                        }}
                                        startDate={commonService.getDateInDBFormat(startDate)}
                                        endDate={commonService.getDateInDBFormat(endDate)}
                                        demandScoreData={demandScoreData}
                                        loadingDemand={loadingDemand}
                                        asOfDate={asOfDate}
                                        demandLevels={demandLevels}
                                        api={api}
                                        para={para}
                                    />
                                )}
                            </Tab>
                          {/*  <Tab eventKey="KPI Weights" title="KPI Weights">
                                <KPIWeights data={kpiData} onUpdate={handleKPIUpdate} />
                            </Tab> */}
                            <Tab eventKey="Booking" title="Booking Windows">
                                <BookingWindow data={kpiData} onUpdate={handleKPIUpdate} />
                            </Tab>
                            <Tab eventKey="Cancelation" title="Cancellation">
                                {activeTab === "Cancelation" && (
                                    <Cancellation
                                        data={cancellationConfig}
                                        demandScoreData={demandScoreData}
                                        onUpdate={handleCancellationUpdate}
                                        loading={loadingDemand}
                                    />
                                )}
                            </Tab>
                            <Tab eventKey="Demand Levels" title="Demand Levels">
                                <DemandLevels
                                    data={demandLevels}
                                    onUpdate={handleDemandLevelsUpdate}
                                />
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
                </Container>
            ) : (
                <Container>
                    <div className="d-flex justify-content-center">
                        <h5>Please Select Property</h5>
                    </div>
                </Container>
            )}
        </div>
    );
};

export default DemandScore;
